import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
  BarChart,
  Bar
} from 'recharts';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper
} from '@mui/material';
import dayjs from 'dayjs';

const TrendsChart = ({ data }) => {
  const [chartType, setChartType] = React.useState('line');

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 300,
        flexDirection: 'column',
        color: 'text.secondary'
      }}>
        <Typography variant="h6" gutterBottom>
          No trend data available
        </Typography>
        <Typography variant="body2">
          Data will appear once shipments are created
        </Typography>
      </Box>
    );
  }

  // Format the data for better display
  const formattedData = data.map(item => ({
    ...item,
    formattedDate: dayjs(item.date).format('MMM DD'),
    fullDate: dayjs(item.date).format('MMMM DD, YYYY'),
    deliveryRate: Number(item.delivery_rate.toFixed(1))
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom>
            {data.fullDate}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#8884d8' }}>
                Total Shipments:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.shipments}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#82ca9d' }}>
                Delivered:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.delivered}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#ffc658' }}>
                Delivery Rate:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.deliveryRate}%
              </Typography>
            </Box>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: formattedData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="formattedDate" 
              stroke="#666"
              fontSize={12}
            />
            <YAxis stroke="#666" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey="shipments"
              stackId="1"
              stroke="#8884d8"
              fill="#8884d8"
              fillOpacity={0.3}
              name="Total Shipments"
            />
            <Area
              type="monotone"
              dataKey="delivered"
              stackId="2"
              stroke="#82ca9d"
              fill="#82ca9d"
              fillOpacity={0.3}
              name="Delivered"
            />
          </AreaChart>
        );
      
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="formattedDate" 
              stroke="#666"
              fontSize={12}
            />
            <YAxis stroke="#666" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="shipments" fill="#8884d8" name="Total Shipments" />
            <Bar dataKey="delivered" fill="#82ca9d" name="Delivered" />
          </BarChart>
        );
      
      default: // line
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="formattedDate" 
              stroke="#666"
              fontSize={12}
            />
            <YAxis yAxisId="left" stroke="#666" fontSize={12} />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              stroke="#666" 
              fontSize={12}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="shipments"
              stroke="#8884d8"
              strokeWidth={3}
              dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#8884d8', strokeWidth: 2 }}
              name="Total Shipments"
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="delivered"
              stroke="#82ca9d"
              strokeWidth={3}
              dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#82ca9d', strokeWidth: 2 }}
              name="Delivered"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="deliveryRate"
              stroke="#ffc658"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#ffc658', strokeWidth: 2, r: 3 }}
              name="Delivery Rate (%)"
            />
          </LineChart>
        );
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Shipment trends over time
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing {formattedData.length} data points
          </Typography>
        </Box>
        
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
        >
          <ToggleButton value="line">
            Line
          </ToggleButton>
          <ToggleButton value="area">
            Area
          </ToggleButton>
          <ToggleButton value="bar">
            Bar
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ResponsiveContainer width="100%" height={300}>
        {renderChart()}
      </ResponsiveContainer>

      {/* Summary Stats */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-around', 
        mt: 2, 
        p: 2, 
        bgcolor: 'background.default',
        borderRadius: 1
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="primary">
            {formattedData.reduce((sum, item) => sum + item.shipments, 0).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total Shipments
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="success.main">
            {formattedData.reduce((sum, item) => sum + item.delivered, 0).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total Delivered
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="info.main">
            {Math.max(...formattedData.map(item => item.shipments))}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Peak Day
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main">
            {(formattedData.reduce((sum, item) => sum + item.deliveryRate, 0) / formattedData.length).toFixed(1)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Avg Rate
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TrendsChart;